// src/hooks/useColumnResize.ts
import { useCallback, useEffect, useRef, useState } from 'react';

type ResizeOpts = { edge?: 'left' | 'right'; enabled?: boolean; fallbackWidth?: number };

export function useColumnResize(key: string, initial = 280, min = 120, max = 1200, opts?: ResizeOpts) {
    const LS_KEY = `wbs:col:${key}`;
    const enabled = opts?.enabled ?? true;
    const fallbackWidth = opts?.fallbackWidth ?? initial;

    const [width, setWidth] = useState<number>(() => {
        if (typeof window === 'undefined') return initial;
        const saved = Number(localStorage.getItem(LS_KEY));
        return Number.isFinite(saved) && saved > 0 ? saved : initial;
    });

    const dragging = useRef(false);
    const startX = useRef(0);
    const startW = useRef(width);

    const onMouseDown = useCallback(
        (e: React.MouseEvent) => {
            if (!enabled) return;
            dragging.current = true;
            startX.current = e.clientX;
            startW.current = width;
            // 드래그 중 텍스트 선택 방지
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'col-resize';
        },
        [enabled, width],
    );

    const onMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!dragging.current) return;
            // const dx = e.clientX - startX.current;
            const sign = opts?.edge === 'right' ? -1 : 1;
            const dx = (e.clientX - startX.current) * sign;
            const next = Math.max(min, Math.min(max, startW.current + dx));
            setWidth(next);
        },
        [min, max, opts?.edge],
    );

    const onMouseUp = useCallback(() => {
        if (!dragging.current) return;
        dragging.current = false;
        if (typeof window !== 'undefined') {
            localStorage.setItem(LS_KEY, String(width));
        }
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
    }, [LS_KEY, width]);

    useEffect(() => {
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [onMouseMove, onMouseUp]);

    return { width: enabled ? width : fallbackWidth, onMouseDown, enabled };
}
